import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { BookOpen, ArrowRight } from 'lucide-react';
import { Card } from '../ui/card';

export const KnowledgeCenter = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articles = [
    {
      title: 'Why Construction and Shutdown Paychecks Are Often Wrong',
      description: 'Learn why construction and refinery paychecks go wrong and how real-time visibility prevents errors.',
      url: '/knowledge/paychecks',
      category: 'Payroll Accuracy',
      readTime: '5 min read'
    },
    {
      title: 'How Overtime and Per Diem Actually Work',
      description: 'Understanding complex OT rules and per diem calculations in construction and shutdowns.',
      url: '/knowledge/overtime-perdiem',
      category: 'Overtime & Per Diem',
      readTime: '6 min read'
    },
    {
      title: 'Pay Clarity: Why Tradespeople Deserve to Know Their Check Before Payday',
      description: 'The emotional and financial impact of paycheck uncertainty and how real-time tracking solves it.',
      url: '/knowledge/pay-clarity',
      category: 'Pay Transparency',
      readTime: '4 min read'
    },
    {
      title: 'Why General Time-Tracking Apps Fail Tradespeople',
      description: 'Why office-oriented time apps cannot handle the complexity of construction work.',
      url: '/knowledge/general-apps-vs-payflow',
      category: 'App Comparison',
      readTime: '5 min read'
    },
    {
      title: 'Why Shutdown and Refinery Pay Is Hard to Predict',
      description: 'The hidden complexities of shutdown pay and why workers need specialized tracking tools.',
      url: '/knowledge/refinery-pay-issues',
      category: 'Refinery & Shutdowns',
      readTime: '5 min read'
    }
  ];

  return (
    <>
      <Helmet>
        <title>PayFlow Knowledge Center | Construction Payroll Insights</title>
        <meta 
          name="description" 
          content="Expert insights on construction payroll, overtime tracking, per diem, and paycheck accuracy for tradespeople and refinery workers." 
        />
      </Helmet>

      <div className="min-h-screen bg-[#0D1115] pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#30D5C8]/10 border border-[#30D5C8]/30 rounded-full px-4 py-2 mb-6">
              <span className="text-[#30D5C8] text-sm font-['Montserrat'] font-semibold">
                KNOWLEDGE CENTER
              </span>
            </div>
            <h1 className="font-['Montserrat'] font-bold text-5xl sm:text-6xl text-[#F5F7F8] mb-6">
              PayFlow <span className="text-[#30D5C8]">Knowledge Center</span>
            </h1>
            <p className="text-[#CBD3D9] text-xl font-['Open_Sans'] max-w-3xl mx-auto leading-relaxed">
              Expert insights on construction payroll, overtime tracking, per diem, and paycheck accuracy for tradespeople and refinery workers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {articles.map((article, index) => (
              <Card
                key={index}
                className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 hover:border-[#30D5C8]/50 transition-all duration-300 card-hover group overflow-hidden"
              >
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[#30D5C8] text-xs font-['Montserrat'] font-semibold uppercase tracking-wide">
                      {article.category}
                    </span>
                    <span className="text-[#506273] text-xs font-['Open_Sans']">
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-4 leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-[#CBD3D9] font-['Open_Sans'] mb-6 leading-relaxed">
                    {article.description}
                  </p>
                  <a
                    href={article.url}
                    className="inline-flex items-center text-[#30D5C8] hover:text-[#30D5C8]/80 font-['Montserrat'] font-semibold transition-colors group"
                  >
                    Read Article
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                  </a>
                </div>
              </Card>
            ))}
          </div>

          <Card className="bg-gradient-to-br from-[#30D5C8]/20 to-[#30D5C8]/5 border-[#30D5C8]/50 p-12 text-center">
            <BookOpen className="w-16 h-16 text-[#30D5C8] mx-auto mb-6" />
            <h2 className="font-['Montserrat'] font-bold text-3xl text-[#F5F7F8] mb-4">
              Want Paycheck Clarity Before Payday?
            </h2>
            <p className="text-[#CBD3D9] text-lg font-['Open_Sans'] mb-8 max-w-2xl mx-auto">
              PayFlow gives construction and refinery workers real-time visibility into hours, overtime, and per diem.
            </p>
            <a
              href="/payflow"
              className="inline-block bg-[#30D5C8] text-[#0D1115] hover:bg-[#30D5C8]/90 font-['Montserrat'] font-bold text-lg px-10 py-5 rounded-lg transition-colors"
            >
              Explore PayFlow App
            </a>
          </Card>
        </div>
      </div>
    </>
  );
};
